import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Play, CheckCircle, Lock, ChevronRight, ChevronLeft, Award,
  BookOpen, Video, HelpCircle, FileCheck, Star, Zap, Clock,
  Volume2, VolumeX, RefreshCw, AlertTriangle, Trophy, CreditCard, ShoppingCart,
  Eye, Edit3, ArrowLeftCircle, Sparkles
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { KnowledgeChecks, LessonVideo } from '../components/LessonEnhancements';
import { CourseIntroVideo } from '../components/CourseIntroVideo';
import { KeyPointsBox } from '../components/KeyPointsBox';
import { useLessonTracking } from '../hooks/useLessonTracking';

import { apiFetch } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// KI-MODERATORIN LISA KOMPONENTE
// ============================================================================
const LisaIntro = ({ script, onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);
  const prefetchStartedRef = useRef(false);

  // Preload TTS-Audio direkt beim Mount, damit der Play-Button
  // synchron im User-Klick-Handler abspielen kann (Chrome-Autoplay-Policy).
  useEffect(() => {
    if (!script || prefetchStartedRef.current) return;
    prefetchStartedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API}/api/learning/tts/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: script })
        });
        const data = await res.json();
        if (!cancelled && data.success && data.audio_base64) {
          setAudioUrl(`data:audio/mp3;base64,${data.audio_base64}`);
          setAudioReady(true);
        } else if (!cancelled) {
          setAudioError(true);
        }
      } catch (e) {
        if (!cancelled) setAudioError(true);
        console.error('LisaIntro TTS prefetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [script]);

  // Zeichen-fuer-Zeichen Anzeige (Typewriter-Effekt)
  useEffect(() => {
    if (isPlaying && currentIndex < script.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + script[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 30);
      return () => clearTimeout(timer);
    } else if (currentIndex >= script.length && isPlaying) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentIndex, script]);

  // Aufraeumen beim Unmount / Lektionswechsel
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch { /* noop */ }
        audioRef.current = null;
      }
    };
  }, []);

  const handleStart = () => {
    // WICHTIG: `new Audio()` + `.play()` muessen SYNCHRON im
    // User-Gesture-Handler passieren, sonst blockiert Chrome die Wiedergabe.
    setIsPlaying(true);
    setDisplayedText('');
    setCurrentIndex(0);

    if (audioUrl) {
      try {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.play().catch((err) => {
          // Autoplay blockiert - Text laeuft trotzdem weiter.
          console.debug('LisaIntro audio.play blocked:', err?.message || err);
        });
      } catch (err) {
        console.debug('LisaIntro audio construction failed:', err);
      }
      return;
    }

    // Falls Prefetch noch nicht fertig: zeigen wir Loader,
    // triggern noch mal explizit und spielen ab wenn bereit.
    if (!audioError && !audioLoading) {
      setAudioLoading(true);
      (async () => {
        try {
          const res = await apiFetch(`${API}/api/learning/tts/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: script })
          });
          const data = await res.json();
          if (data.success && data.audio_base64) {
            const url = `data:audio/mp3;base64,${data.audio_base64}`;
            setAudioUrl(url);
            setAudioReady(true);
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.play().catch(() => {});
          } else {
            setAudioError(true);
          }
        } catch (e) {
          setAudioError(true);
          console.error('LisaIntro TTS on-demand failed:', e);
        } finally {
          setAudioLoading(false);
        }
      })();
    }
  };

  const handleSkip = () => {
    setDisplayedText(script);
    setCurrentIndex(script.length);
    setIsPlaying(false);
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-500/30" data-testid="lisa-intro">
      <div className="flex items-start gap-6">
        {/* Lisa Avatar */}
        <div className="flex-shrink-0">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-4xl shadow-lg shadow-purple-500/30 relative">
            👩‍🏫
            {isPlaying && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                <Volume2 className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <p className="text-center text-purple-300 text-sm mt-2 font-medium">Lisa</p>
        </div>

        {/* Speech Bubble */}
        <div className="flex-1">
          <div className="bg-black/40 rounded-xl p-6 relative">
            <div className="absolute -left-3 top-6 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-black/40" />

            {!isPlaying && currentIndex === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-300 mb-4">
                  {audioError
                    ? 'Klicken Sie auf Play, um die Einführung zu starten (Audio nicht verfügbar)'
                    : audioReady
                      ? 'Klicken Sie auf Play, um die Einführung zu starten'
                      : 'Klicken Sie auf Play, um die Einführung zu starten'}
                </p>
                <Button
                  onClick={handleStart}
                  disabled={audioLoading}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500"
                  data-testid="lisa-start-btn"
                >
                  {audioLoading ? (
                    <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Audio wird geladen...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Lisa starten</>
                  )}
                </Button>
              </div>
            ) : (
              <>
                <p className="text-gray-200 whitespace-pre-line leading-relaxed min-h-[120px]">
                  {displayedText}
                  {isPlaying && <span className="animate-pulse">|</span>}
                </p>
                
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                  {isPlaying ? (
                    <Button variant="outline" size="sm" onClick={handleSkip} className="text-gray-400 border-gray-600">
                      Überspringen
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleStart} className="text-gray-400 border-gray-600">
                      <RefreshCw className="w-3 h-3 mr-1" /> Nochmal
                    </Button>
                  )}
                  
                  {!isPlaying && currentIndex > 0 && (
                    <Button 
                      onClick={onComplete}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                    >
                      Weiter zur Lektion <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUIZ KOMPONENTE
// ============================================================================
const QuizComponent = ({ questions, onSubmit, isPassing = false, passThreshold = 80, onNavigateToLesson }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  // Wenn der Lerner „Nur falsche Fragen wiederholen" nutzt, filtern wir hier
  // die Frage-Liste beim nächsten Versuch – ohne den ganzen Kurs neu zu starten.
  const [retryQuestionIds, setRetryQuestionIds] = useState(null);

  // Defensive: Fragen bereinigen (null, ohne id, ohne options → raus)
  const safeQuestions = useMemo(() => {
    if (!Array.isArray(questions)) return [];
    return questions.filter((q) => q && q.id && Array.isArray(q.options) && q.options.length > 0);
  }, [questions]);

  // Fragen für den aktuellen Versuch (voll oder nur die falschen)
  const activeQuestions = useMemo(() => {
    if (!retryQuestionIds) return safeQuestions;
    const set = new Set(retryQuestionIds);
    return safeQuestions.filter((q) => set.has(q.id));
  }, [safeQuestions, retryQuestionIds]);

  const handleSelectOption = (questionId, optionId) => {
    if (submitted) return;
    
    const question = questions.find(q => q.id === questionId);
    if (question.question_type === 'single') {
      setAnswers(prev => ({ ...prev, [questionId]: [optionId] }));
    } else {
      const current = answers[questionId] || [];
      if (current.includes(optionId)) {
        setAnswers(prev => ({ ...prev, [questionId]: current.filter(id => id !== optionId) }));
      } else {
        setAnswers(prev => ({ ...prev, [questionId]: [...current, optionId] }));
      }
    }
  };

  const handleSubmit = async () => {
    const formattedAnswers = Object.entries(answers).map(([questionId, selectedOptions]) => ({
      question_id: questionId,
      selected_options: selectedOptions
    }));

    let result;
    try {
      result = await onSubmit(formattedAnswers);
    } catch (err) {
      // Netzwerk-/Runtime-Fehler → sauberer Fehler-State statt Crash
      result = {
        success: false,
        error: true,
        message: err?.message || 'Fehler bei der Auswertung – bitte erneut versuchen.',
      };
    }

    // Backend-Fehler-Responses (401/404/500 mit `{detail:...}`) abfangen —
    // sonst crasht das UI später an `results.score.toFixed()`.
    if (!result || typeof result !== 'object') {
      result = { success: false, error: true, message: 'Keine Antwort vom Server.' };
    }
    if (typeof result.score !== 'number') {
      // Wenn der Server einen Fehler gemeldet hat, behalten wir seine Nachricht.
      const hasErrorMsg = result.detail || result.message;
      result = {
        success: false,
        error: true,
        message: hasErrorMsg || 'Die Auswertung konnte nicht geladen werden.',
      };
    }

    setResults(result);
    setSubmitted(true);
  };

  const allAnswered = activeQuestions.every(q => answers[q.id] && answers[q.id].length > 0);

  // ERROR-STATE: Backend gab einen Fehler zurück (z. B. 401, keine Fragen etc.).
  // Zeige eine freundliche Meldung + Retry-Button statt eines White-Screens.
  if (submitted && results && results.error) {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/30 space-y-4" data-testid="quiz-error-state">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-8 h-8 text-red-400 shrink-0 mt-1" />
          <div>
            <h3 className="text-red-300 font-bold text-lg mb-1">Auswertung fehlgeschlagen</h3>
            <p className="text-gray-300 text-sm">{results.message}</p>
          </div>
        </div>
        <Button
          onClick={() => { setSubmitted(false); setResults(null); }}
          variant="outline"
          className="border-red-500/40 text-red-300 hover:bg-red-500/10"
          data-testid="quiz-error-retry-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Nochmal versuchen
        </Button>
      </div>
    );
  }

  if (submitted && results) {
    // Score-Berechnung & richtige/falsche Fragen ermitteln
    const scoreNum = Number(results.score ?? 0);
    const scorePct = Math.round(scoreNum);
    const perQuestion = Array.isArray(results?.results) ? results.results : [];
    const wrongIds = perQuestion.filter(r => r && !r.correct).map(r => r.question_id).filter(Boolean);
    const wrongCount = wrongIds.length;
    const totalQ = activeQuestions.length || 1;
    const pointsMissing = Math.max(0, passThreshold - scorePct);

    // Ermutigendes Coaching-Kopfstück (kein rot-schock)
    const passed = !!results.passed;

    return (
      <div className="space-y-6" data-testid="quiz-results">
        {/* COACHING BANNER — warmes Feedback statt Rot-Schock */}
        <div
          data-testid={passed ? 'quiz-banner-passed' : 'quiz-banner-coaching'}
          className={`p-6 rounded-2xl border-2 ${
            passed
              ? 'bg-gradient-to-r from-green-500/15 to-emerald-500/10 border-green-500/40'
              : 'bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-orange-500/5 border-yellow-500/40'
          }`}
        >
          <div className="flex items-start gap-4 flex-wrap">
            {passed ? (
              <CheckCircle className="w-12 h-12 text-green-400 shrink-0" />
            ) : (
              <Sparkles className="w-12 h-12 text-yellow-400 shrink-0" />
            )}
            <div className="flex-1 min-w-[220px]">
              <h3 className={`text-2xl font-black leading-tight ${passed ? 'text-green-300' : 'text-yellow-300'}`}>
                {passed ? '🎉 Glückwunsch — bestanden!' : 'Fast geschafft!'}
              </h3>
              <p className="text-gray-200 mt-1 text-sm sm:text-base">
                {passed
                  ? `Du hast ${scorePct}% erreicht (benötigt: ${passThreshold}%). Weiter so!`
                  : wrongCount > 0
                    ? `Du hast ${scorePct}% — es fehlen dir nur ${pointsMissing} Prozentpunkte. ${wrongCount} von ${totalQ} Fragen sind noch nicht ganz richtig. Schau dir die Erklärungen unten an — du hast das gleich!`
                    : `Du hast ${scorePct}% erreicht (benötigt: ${passThreshold}%). Ein kleines Stück fehlt noch — du schaffst das!`
                }
              </p>
              {/* Fortschritts-Balken zum Ziel */}
              <div className="mt-3">
                <div className="h-2 rounded-full bg-black/40 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${passed ? 'bg-green-400' : 'bg-yellow-400'}`}
                    style={{ width: `${Math.min(100, scorePct)}%` }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                  <span>0%</span>
                  <span className={passed ? 'text-green-300 font-bold' : 'text-yellow-300 font-bold'}>
                    Ziel: {passThreshold}%
                  </span>
                  <span>100%</span>
                </div>
              </div>
              {results.points_earned > 0 && (
                <p className="text-yellow-400 mt-2 text-sm font-semibold">+{results.points_earned} SmartVolt-Points</p>
              )}
            </div>
          </div>
        </div>

        {/* Retry-Optionen NUR bei nicht bestanden */}
        {!passed && wrongCount > 0 && (
          <div
            data-testid="quiz-retry-options"
            className="p-5 rounded-xl bg-gray-900/60 border border-yellow-500/30 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between"
          >
            <div className="text-sm">
              <p className="text-white font-bold">So kommst du zum Ziel:</p>
              <p className="text-gray-400">Wiederhole gezielt die {wrongCount} offenen Fragen — deine richtigen Antworten bleiben.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => {
                  setRetryQuestionIds(wrongIds);
                  // richtige Antworten der falschen Fragen zurücksetzen
                  setAnswers(prev => {
                    const next = { ...prev };
                    wrongIds.forEach(id => { delete next[id]; });
                    return next;
                  });
                  setSubmitted(false);
                  setResults(null);
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                }}
                className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold hover:from-yellow-400 hover:to-amber-400"
                data-testid="quiz-retry-wrong-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Nur falsche Fragen wiederholen ({wrongCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setRetryQuestionIds(null);
                  setAnswers({});
                  setSubmitted(false);
                  setResults(null);
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                }}
                className="border-gray-600 text-gray-200 hover:bg-gray-800"
                data-testid="quiz-retry-all-btn"
              >
                Kompletten Test neu starten
              </Button>
            </div>
          </div>
        )}

        {/* Zertifikat-Download nach bestandenem Abschlusstest */}
        {passed && results.certificate && (
          <div
            className="p-6 rounded-xl bg-gradient-to-br from-yellow-500/15 to-amber-500/10 border border-yellow-500/40"
            data-testid="certificate-download-block"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="text-5xl">🎓</div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xl font-bold text-yellow-300 mb-1">
                  {results.certificate.id === 'PREVIEW-ONLY' ? 'Vorschau-Zertifikat verfügbar' : 'Ihr Zertifikat ist bereit!'}
                </h4>
                <p className="text-gray-300 text-sm mb-2">
                  {results.certificate.id === 'PREVIEW-ONLY'
                    ? 'Als Admin-Preview: unten kannst du das PDF-Layout mit Beispiel-Daten prüfen (kein DB-Insert, kein rechtsgültiges Zertifikat).'
                    : 'Ihr rechtssicheres Zertifikat wurde erstellt. Öffne oder speichere es jetzt als PDF.'}
                </p>
                <p className="text-xs text-gray-500 font-mono">Nr.: {results.certificate.certificateNumber}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  onClick={() => {
                    const url = results.certificate.downloadUrl
                      ? `${process.env.REACT_APP_BACKEND_URL}${results.certificate.downloadUrl}`
                      : `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${results.certificate.id}/preview`;
                    window.open(url, '_blank');
                  }}
                  className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold hover:from-yellow-400 hover:to-amber-400"
                  data-testid="cert-preview-btn"
                >
                  📄 Zertifikat öffnen
                </Button>
                {results.certificate.id !== 'PREVIEW-ONLY' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${results.certificate.id}/download`;
                      window.open(url, '_blank');
                    }}
                    className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                    data-testid="cert-download-btn"
                  >
                    ⬇ PDF herunterladen
                  </Button>
                )}
                {/* 1-Klick LinkedIn-Share (fuer echte Zertifikate) */}
                {results.certificate.id !== 'PREVIEW-ONLY' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const courseTitle = results.certificate.courseTitle || 'DGUV-V3-Schulung';
                      const backend = process.env.REACT_APP_BACKEND_URL;
                      const verifyUrl = `${backend}/api/certificates/verify/${results.certificate.certificateNumber || results.certificate.id}`;
                      const text = encodeURIComponent(
                        `Ich habe soeben die "${courseTitle}" bei SmartVolt Academy erfolgreich absolviert! ` +
                        `Rechtssicheres PDF-Zertifikat inklusive. #Elektrotechnik #DGUV #Weiterbildung #SmartVoltAcademy`
                      );
                      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}&text=${text}`;
                      window.open(url, '_blank', 'width=650,height=580');
                    }}
                    className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                    data-testid="cert-linkedin-share-btn"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    Auf LinkedIn teilen
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detail-Feedback pro Frage — didaktisch, nicht schockierend */}
        <div className="space-y-4" data-testid="quiz-per-question-feedback">
          {activeQuestions.map((question, idx) => {
            const result = perQuestion.find(r => r?.question_id === question.id) || null;
            const isCorrect = !!result?.correct;
            const correctIds = Array.isArray(result?.correct_options) ? result.correct_options : [];
            const options = Array.isArray(question.options) ? question.options : [];
            const correctOptionsFull = options.filter(o => correctIds.includes(o.id));
            const explanation = result?.explanation || question?.explanation || '';

            return (
              <div
                key={question.id || `q-${idx}`}
                data-testid={`quiz-feedback-${question.id}`}
                className={`p-5 rounded-xl border-2 ${
                  isCorrect
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-amber-500/[0.07] border-amber-500/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  {isCorrect ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shrink-0 mt-0.5">
                      <BookOpen className="w-5 h-5 text-black" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">
                      {idx + 1}. {question.question}
                    </p>
                    {!isCorrect && correctOptionsFull.length > 0 && (
                      <div className="mt-3 rounded-lg bg-black/30 border border-green-500/30 p-3">
                        <p className="text-[11px] uppercase tracking-widest text-green-300 font-black mb-1">
                          Richtige Antwort
                        </p>
                        <ul className="space-y-1">
                          {correctOptionsFull.map((o) => (
                            <li key={o.id} className="text-green-200 text-sm flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                              <span>
                                <strong className="text-green-300">{o.id.toUpperCase()})</strong> {o.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {explanation && (
                      <div className="mt-3 rounded-lg bg-yellow-500/[0.06] border border-yellow-500/30 p-3">
                        <p className="text-[11px] uppercase tracking-widest text-yellow-300 font-black mb-1">
                          Warum ist das so?
                        </p>
                        <p className="text-gray-200 text-sm leading-relaxed">{explanation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Remediation - Clickable lessons to navigate */}
        {!passed && results.remediation_lessons?.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4" data-testid="remediation-section">
            <h4 className="text-yellow-400 font-medium mb-3">Empfohlene Wiederholung:</h4>
            <p className="text-gray-400 text-sm mb-3">
              Diese Lektionen helfen dir gezielt bei den offenen Punkten.
            </p>
            <div className="space-y-2">
              {results.remediation_lessons.map(lesson => (
                <button
                  key={lesson.id}
                  onClick={() => onNavigateToLesson && onNavigateToLesson(lesson.id)}
                  className="w-full text-left p-3 bg-gray-800/50 hover:bg-yellow-500/10 border border-gray-700 hover:border-yellow-500/50 rounded-lg transition-all flex items-center gap-3"
                  data-testid={`remediation-lesson-${lesson.id}`}
                >
                  <BookOpen className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  <span className="text-gray-200 text-sm">{lesson.title}</span>
                  <ChevronRight className="w-4 h-4 text-gray-500 ml-auto" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {activeQuestions.map((question, idx) => (
        <div key={question.id} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h4 className="text-white font-medium mb-4">
            <span className="text-yellow-500">{idx + 1}.</span> {question.question}
          </h4>
          
          <div className="space-y-2">
            {(question.options || []).map(option => {
              const isSelected = answers[question.id]?.includes(option.id);
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelectOption(question.id, option.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all ${
                    isSelected 
                      ? 'bg-yellow-500/20 border-2 border-yellow-500' 
                      : 'bg-gray-700/50 border border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <span className={`font-medium ${isSelected ? 'text-yellow-400' : 'text-gray-300'}`}>
                    {option.id.toUpperCase()})
                  </span>{' '}
                  <span className="text-gray-200">{option.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <Button
        onClick={handleSubmit}
        disabled={!allAnswered}
        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-6 text-lg disabled:opacity-50"
      >
        Antworten einreichen
      </Button>
    </div>
  );
};

// ============================================================================
// KURS-PLAYER HAUPTKOMPONENTE
// ============================================================================
// ============================================================================
// KURSEMPFEHLUNGEN / UPSELL KOMPONENTE
// ============================================================================
const CourseRecommendations = ({ moduleId }) => {
  const navigate = useNavigate();
  const [recs, setRecs] = useState({ recommendations: [], bundle: null });

  useEffect(() => {
    if (!moduleId) return;
    apiFetch(`${API}/api/learning/recommendations/${moduleId}`)
      .then(r => r.json())
      .then(d => { if (d.success) setRecs(d); })
      .catch(() => {});
  }, [moduleId]);

  if (!recs.recommendations?.length && !recs.bundle) return null;

  const catColors = {
    basis: 'border-yellow-500/30', pv: 'border-orange-500/30', smart_home: 'border-cyan-500/30',
    buero: 'border-purple-500/30', ki_handwerk: 'border-emerald-500/30'
  };
  const catBadge = {
    basis: 'bg-yellow-500/20 text-yellow-400', pv: 'bg-orange-500/20 text-orange-400',
    smart_home: 'bg-cyan-500/20 text-cyan-400', buero: 'bg-purple-500/20 text-purple-400',
    ki_handwerk: 'bg-emerald-500/20 text-emerald-400'
  };

  return (
    <Card className="bg-gray-900/50 border-gray-800 mt-4" data-testid="course-recommendations">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-500" /> Passende Kurse
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {recs.bundle && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span className="text-yellow-400 font-bold text-sm">{recs.bundle.name}</span>
            </div>
            <p className="text-xs text-gray-400">{recs.bundle.discount} Rabatt auf das Paket</p>
          </div>
        )}
        {recs.recommendations.slice(0, 3).map(rec => (
          <button key={rec.id}
            onClick={() => navigate(`/kurs/${rec.id}`)}
            className={`w-full text-left p-2.5 rounded-lg border ${catColors[rec.category] || 'border-gray-700'} hover:bg-gray-800/50 transition`}
          >
            <p className="text-sm text-gray-300 truncate">{rec.title}</p>
            <Badge className={`${catBadge[rec.category] || 'bg-gray-700 text-gray-400'} text-xs mt-1`}>
              {rec.category?.replace('_', ' ')}
            </Badge>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};

const CoursePlayer = () => {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  
  // Admin-Qualitätscheck-Modus: aktiviert per ?admin=1 in der URL.
  // Der Backend-Endpoint prüft zusätzlich, dass der Requester wirklich
  // Admin ist (E-Mail-Match + role=admin). Rein clientseitige URL-Tricks
  // reichen NICHT für einen Bypass.
  const isAdminPreview = searchParams.get('admin') === '1';
  const adminQuery = isAdminPreview ? '&admin_preview=1' : '';

  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [accessInfo, setAccessInfo] = useState({ has_full_access: false, is_free_trial: false, free_lesson_count: 99 });
  const [lessonLocked, setLessonLocked] = useState(false);
  const [adminPreviewConfirmed, setAdminPreviewConfirmed] = useState(false);
  // Wissenscheck-Status pro Lektion: true wenn alle Zwischenfragen richtig
  // beantwortet wurden (bzw. keine Zwischenfragen vorhanden sind).
  const [knowledgeChecksPassed, setKnowledgeChecksPassed] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Lesson-Tracking-Hook: sendet lesson_start / _progress / _exit automatisch
  const currentLessonId = currentLesson?.id;
  const lessonTracker = useLessonTracking(moduleId, currentLessonId);

  useEffect(() => {
    loadModuleAndLessons();
  }, [moduleId]);

  useEffect(() => {
    if (lessonId && lessons.length > 0) {
      loadLesson(lessonId);
    }
  }, [lessonId, lessons]);

  const loadModuleAndLessons = async () => {
    try {
      const params = new URLSearchParams();
      if (isAdminPreview) params.set('admin_preview', '1');
      const qs = params.toString();
      const url = `${API_URL}/api/learning/modules/${moduleId}${qs ? `?${qs}` : ''}`;
      const response = await apiFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setModule(data.module);
        // Im Admin-Preview-Modus: Backend confirmed → is_locked wurde bereits
        // auf false gesetzt; wir markieren zusätzlich lokal, damit das UI
        // die Sidebar-Buttons freischaltet.
        const confirmed = !!data.is_admin_preview;
        setAdminPreviewConfirmed(confirmed);
        setLessons(data.lessons.map((l) => confirmed ? { ...l, is_locked: false } : l));
        setAccessInfo({
          has_full_access: data.has_full_access,
          is_free_trial: data.is_free_trial,
          free_lesson_count: data.free_lesson_count
        });
        
        if (!lessonId && data.lessons.length > 0) {
          const suffix = isAdminPreview ? '?admin=1' : '';
          navigate(`/kurs/${moduleId}/${data.lessons[0].id}${suffix}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error loading module:', error);
      toast({ title: "Fehler", description: "Modul konnte nicht geladen werden", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadLesson = async (id) => {
    const lesson = lessons.find(l => l.id === id);
    // Admin-Preview überspringt Lock-Check komplett
    if (lesson && lesson.is_locked && !adminPreviewConfirmed) {
      setLessonLocked(true);
      setCurrentLesson(lesson);
      return;
    }
    setLessonLocked(false);
    
    try {
      const params = new URLSearchParams();
      if (isAdminPreview) params.set('admin_preview', '1');
      const qs = params.toString();
      const url = `${API_URL}/api/learning/modules/${moduleId}/lessons/${id}${qs ? `?${qs}` : ''}`;

      const response = await apiFetch(url);
      
      if (response.status === 403) {
        setLessonLocked(true);
        setCurrentLesson(lesson || { id, title: 'Gesperrt', lesson_type: 'content' });
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCurrentLesson(data.lesson);
        setProgress(data.progress);
        setShowIntro(data.lesson.lesson_type === 'intro');
        // Wissens-Check-Status: Wenn die Lektion Zwischenfragen hat, muss der
        // Lerner sie erst richtig beantworten, bevor "Lektion abschließen"
        // freigeschaltet wird. Ohne Zwischenfragen → sofort freigeschaltet.
        const hasChecks = Array.isArray(data.lesson?.knowledge_checks) && data.lesson.knowledge_checks.length > 0;
        // Admin-Preview umgeht auch den Wissens-Check-Gate, damit der Chef
        // schnell durch alle Lektionen springen kann.
        setKnowledgeChecksPassed(!hasChecks || isAdminPreview);
        
        // Im Admin-Preview KEIN Progress-Tracking (der Admin ist kein Lerner)
        if (isAuthenticated && !isAdminPreview) {
          apiFetch(`${API_URL}/api/learning/progress/start-lesson`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ module_id: moduleId, lesson_id: id })
          });
        }
      }
    } catch (error) {
      console.error('Error loading lesson:', error);
    }
  };

  const handleCheckout = async (packageType) => {
    try {
      const url = `${API_URL}/api/learning/checkout/${packageType}`;
      const res = await apiFetch(url);
      const data = await res.json();
      if (data.success && data.checkout_url) {
        window.open(data.checkout_url, '_blank');
      } else {
        toast({ title: "Hinweis", description: data.message || "Checkout ist noch nicht konfiguriert. Bitte kontaktieren Sie uns.", variant: "default" });
      }
    } catch {
      toast({ title: "Fehler", description: "Checkout konnte nicht geladen werden", variant: "destructive" });
    }
  };

  const getPackageForModule = () => {
    if (!module) return 'basis';
    const cat = module.category;
    const map = { basis: 'basis', pv: 'pv', smart_home: 'smart_home', buero: 'buero', ki_handwerk: 'ki_handwerk' };
    return map[cat] || 'komplett';
  };

  const completeLesson = async () => {
    if (!isAuthenticated) {
      toast({ title: "Hinweis", description: "Bitte melden Sie sich an, um Ihren Fortschritt zu speichern" });
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/api/learning/progress/complete-lesson`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_id: moduleId, lesson_id: currentLesson.id })
      });
      
      const data = await response.json();
      if (data.success) {
        // Analytics: Lesson abgeschlossen
        try { lessonTracker?.trackLessonComplete?.(100); } catch { /* silent */ }
        toast({ 
          title: "✓ Lektion abgeschlossen!", 
          description: `+${data.points_earned} SmartVolt-Points` 
        });
        goToNextLesson();
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  const submitQuiz = async (answers) => {
    if (!isAuthenticated) {
      toast({ title: "Hinweis", description: "Bitte melden Sie sich an" });
      return { passed: false, score: 0 };
    }

    // Im Admin-Vorschau-Modus geht der Final-Quiz-Submit an einen speziellen
    // Admin-Preview-Endpoint (Cookie-Auth), der NICHTS in der DB speichert
    // und ein Sample-Zertifikat zur PDF-Vorschau zurückgibt.
    const endpoint = isAdminPreview && currentLesson.lesson_type === 'final_quiz'
      ? '/api/learning/admin-preview/final-quiz-check'
      : (currentLesson.lesson_type === 'final_quiz'
          ? '/api/learning/quiz/final/submit'
          : '/api/learning/quiz/submit');

    try {
      const response = await apiFetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          module_id: moduleId, 
          lesson_id: currentLesson.id,
          answers 
        })
      });

      // Bei HTTP-Fehler: JSON parsen, aber als Fehler-State zurückgeben.
      // (Das QuizComponent zeigt daraus eine freundliche Meldung.)
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: true,
          message: data?.detail || data?.message || `Server-Fehler (${response.status})`,
        };
      }

      // Admin-Preview: preview_certificate → als certificate mappen,
      // damit der UI-Renderer die gleiche Struktur wie beim regulären Flow sieht.
      if (isAdminPreview && data.preview_certificate) {
        data.certificate = { ...data.preview_certificate };
      }

      // Analytics: pro Frage Track-Event „quiz_submitted" mit correct-Flag
      try {
        if (Array.isArray(data?.results)) {
          data.results.forEach(r => {
            lessonTracker?.trackQuizSubmitted?.({
              question_id: r?.question_id,
              correct: !!r?.correct,
            });
          });
        }
        if (data?.passed) {
          lessonTracker?.trackLessonComplete?.(100);
        }
      } catch { /* silent */ }

      return data;
    } catch (error) {
      console.error('Error submitting quiz:', error);
      return { success: false, error: true, message: 'Netzwerkfehler beim Einreichen des Quiz.' };
    }
  };

  const goToNextLesson = () => {
    const currentIndex = lessons.findIndex(l => l.id === currentLesson.id);
    const adminSuffix = isAdminPreview ? '?admin=1' : '';
    // Sanft nach oben scrollen, damit die neue Lektion im Blickfeld startet
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* silent */ }
    if (currentIndex < lessons.length - 1) {
      navigate(`/kurs/${moduleId}/${lessons[currentIndex + 1].id}${adminSuffix}`);
    } else {
      toast({ title: "🎓 Modul abgeschlossen!", description: "Sie haben alle Lektionen durchgearbeitet." });
      navigate(isAdminPreview ? '/admin' : '/kurse');
    }
  };

  const goToPreviousLesson = () => {
    const currentIndex = lessons.findIndex(l => l.id === currentLesson.id);
    if (currentIndex > 0) {
      const adminSuffix = isAdminPreview ? '?admin=1' : '';
      navigate(`/kurs/${moduleId}/${lessons[currentIndex - 1].id}${adminSuffix}`);
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'intro': return <Volume2 className="w-4 h-4" />;
      case 'content': return <BookOpen className="w-4 h-4" />;
      case 'quiz': return <HelpCircle className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'final_quiz': return <Trophy className="w-4 h-4" />;
      default: return <FileCheck className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Lade Kurs...</p>
        </div>
      </div>
    );
  }

  if (!module || !currentLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <p className="text-gray-400">Kurs nicht gefunden</p>
      </div>
    );
  }

  const currentIndex = lessons.findIndex(l => l.id === currentLesson.id);
  const overallProgress = ((currentIndex + 1) / lessons.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      {/* Admin-Qualitätscheck-Leiste — nur sichtbar wenn Backend den Preview-Modus bestätigt hat */}
      {adminPreviewConfirmed && (
        <div
          className="sticky top-0 z-[60] bg-gradient-to-r from-yellow-500/95 to-amber-500/95 text-black border-b-2 border-yellow-700 backdrop-blur"
          data-testid="admin-preview-bar"
        >
          <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="font-bold text-sm">Admin-Qualitätscheck aktiv</span>
              <span className="text-xs opacity-80 hidden sm:inline">
                — Fehler entdeckt? Direkt in den Editor →
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="h-8 bg-black/10 hover:bg-black/25 text-black font-semibold"
                data-testid="admin-preview-back"
              >
                <ArrowLeftCircle className="h-4 w-4 mr-1.5" /> Zurück zur Kursverwaltung
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/admin?edit-course=${moduleId}&lesson=${currentLesson?.id || ''}`)}
                className="h-8 bg-black text-yellow-300 hover:bg-gray-900 font-semibold"
                data-testid="admin-preview-edit"
                title="Wechselt ins Admin-Panel für Text-Korrekturen"
              >
                <Edit3 className="h-4 w-4 mr-1.5" /> Lektion bearbeiten
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Draft-Banner: Content noch nicht rechtlich freigegeben */}
      {module?.content_status === 'draft' && (
        <div
          className="bg-orange-500/90 text-black border-b-2 border-orange-700"
          data-testid="draft-content-banner"
        >
          <div className="container mx-auto px-4 py-2 flex items-center gap-2 flex-wrap">
            <span className="text-lg" aria-hidden="true">📝</span>
            <span className="font-bold text-sm">Entwurfs-Modus:</span>
            <span className="text-sm">
              Diese Inhalte werden gerade durch unsere Fachkraft final geprüft.
              Verbindliche Rechtssicherheit besteht noch nicht.
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-black/80 border-b border-yellow-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/kurse')}
                className="text-gray-100 border border-gray-700 hover:border-yellow-500/60 hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                data-testid="course-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
              </Button>
              <div>
                <p className="text-sm text-gray-500">{module.category.toUpperCase()}</p>
                <h1 className="text-lg font-bold text-white">{module.title}</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-gray-500">Fortschritt</p>
                <p className="text-yellow-500 font-bold">{currentIndex + 1} / {lessons.length}</p>
              </div>
              <div className="w-32">
                <Progress value={overallProgress} className="h-2" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Lesson Navigation */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <Card className="bg-gray-900/50 border-gray-800 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white text-sm">Lektionen</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1">
                  {lessons.map((lesson, idx) => {
                    const isActive = lesson.id === currentLesson.id;
                    const isCompleted = idx < currentIndex;
                    const isLocked = lesson.is_locked;
                    
                    return (
                      <button
                        key={lesson.id}
                        onClick={() => navigate(`/kurs/${moduleId}/${lesson.id}${isAdminPreview ? '?admin=1' : ''}`)}
                        className={`w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 ${
                          isActive 
                            ? 'bg-yellow-500/20 border border-yellow-500/50' 
                            : isLocked
                              ? 'opacity-50 cursor-not-allowed'
                              : isCompleted
                                ? 'bg-green-500/10 hover:bg-green-500/20'
                                : 'hover:bg-gray-800'
                        }`}
                        data-testid={`lesson-nav-${lesson.id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isLocked ? 'bg-gray-600 text-gray-400' :
                          isActive ? 'bg-yellow-500 text-black' :
                          isCompleted ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
                        }`}>
                          {isLocked ? <Lock className="w-4 h-4" /> : isCompleted ? <CheckCircle className="w-4 h-4" /> : getLessonIcon(lesson.lesson_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${isLocked ? 'text-gray-500' : isActive ? 'text-yellow-400' : 'text-gray-300'}`}>
                            {lesson.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isLocked ? 'Premium' : `${lesson.duration_minutes} Min.`}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Kursempfehlungen */}
            <CourseRecommendations moduleId={moduleId} />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    currentLesson.lesson_type === 'final_quiz' ? 'bg-purple-500/20 text-purple-400' :
                    currentLesson.lesson_type === 'quiz' ? 'bg-blue-500/20 text-blue-400' :
                    currentLesson.lesson_type === 'intro' ? 'bg-pink-500/20 text-pink-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {getLessonIcon(currentLesson.lesson_type)}
                  </div>
                  <div>
                    <CardTitle className="text-white">{currentLesson.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {currentLesson.duration_minutes} Minuten
                      {currentLesson.lesson_type === 'final_quiz' && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Abschlusstest</Badge>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* LOCKED CONTENT - Premium Paywall */}
                {lessonLocked && (
                  <div className="text-center py-12" data-testid="lesson-locked-overlay">
                    <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Lock className="w-10 h-10 text-yellow-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">Premium-Inhalt</h3>
                    <p className="text-gray-400 mb-2 max-w-md mx-auto">
                      {accessInfo.is_free_trial 
                        ? `Die ersten ${accessInfo.free_lesson_count} Lektionen dieses Kurses sind kostenlos. Für den vollen Zugang benötigen Sie ein Abo.`
                        : 'Dieser Kurs ist Teil eines kostenpflichtigen Pakets. Erwerben Sie jetzt Zugang zu allen Inhalten.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                      <Button 
                        onClick={() => handleCheckout(getPackageForModule())}
                        className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold px-8 py-3"
                        data-testid="buy-course-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" /> Jetzt freischalten
                      </Button>
                      <Button 
                        onClick={() => handleCheckout('komplett')}
                        variant="outline"
                        className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                        data-testid="buy-komplett-btn"
                      >
                        Komplett-Paket ansehen
                      </Button>
                      <Button
                        onClick={() => navigate('/login')}
                        variant="ghost"
                        className="text-gray-300 hover:text-white"
                        data-testid="locked-login-btn"
                      >
                        Bereits Kunde? Anmelden
                      </Button>
                    </div>
                    {accessInfo.is_free_trial && (
                      <p className="text-xs text-gray-500 mt-4">
                        Jahressicherheitskurs: 50% kostenlos testen
                      </p>
                    )}
                  </div>
                )}

                {/* INTRO - Lisa Moderatorin */}
                {!lessonLocked && currentLesson.lesson_type === 'intro' && (
                  <>
                    <CourseIntroVideo moduleId={moduleId} />
                    <LisaIntro 
                      script={currentLesson.moderator_script || ''} 
                      onComplete={goToNextLesson}
                    />
                  </>
                )}

                {/* CONTENT - Lerninhalt */}
                {!lessonLocked && currentLesson.lesson_type === 'content' && (
                  <div data-testid="lesson-content-block">
                    {/* Optional: YouTube-Erklärvideo, falls hinterlegt */}
                    <LessonVideo
                      videoUrl={currentLesson.video_url}
                      title={currentLesson.title}
                    />
                    <div
                      className="lesson-content prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: currentLesson.content_html || '' }}
                    />
                    {/* „Das Wichtigste auf einen Blick" — auto-extrahierte Kernpunkte */}
                    <KeyPointsBox contentHtml={currentLesson.content_html || ''} />
                    {/* Zwischenfragen (Knowledge-Checks) — inline, mit Feedback */}
                    <KnowledgeChecks
                      checks={currentLesson.knowledge_checks || []}
                      onAllPassed={() => setKnowledgeChecksPassed(true)}
                    />
                    <div className="mt-8 mb-24 sm:mb-16 lg:mb-8 flex justify-between items-center gap-3 flex-wrap sm:pr-24 lg:pr-0">
                      <Button variant="outline" onClick={goToPreviousLesson} disabled={currentIndex === 0} data-testid="lesson-prev-btn">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                      </Button>
                      <Button
                        onClick={completeLesson}
                        disabled={!knowledgeChecksPassed}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-green-600/20"
                        data-testid="lesson-complete-btn"
                        title={!knowledgeChecksPassed ? 'Bitte zuerst alle Wissens-Check-Fragen richtig beantworten' : ''}
                      >
                        {knowledgeChecksPassed ? 'Lektion abschließen' : 'Bitte Fragen beantworten'}
                        <CheckCircle className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* VIDEO */}
                {!lessonLocked && currentLesson.lesson_type === 'video' && (
                  <div>
                    <LessonVideo
                      videoUrl={currentLesson.video_url}
                      title={currentLesson.title}
                    />
                    <div
                      className="lesson-content prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: currentLesson.content_html || '' }}
                    />
                    <KeyPointsBox contentHtml={currentLesson.content_html || ''} />
                    <KnowledgeChecks
                      checks={currentLesson.knowledge_checks || []}
                      onAllPassed={() => setKnowledgeChecksPassed(true)}
                    />
                    <div className="mt-8 mb-24 sm:mb-16 lg:mb-8 flex justify-end sm:pr-24 lg:pr-0">
                      <Button
                        onClick={completeLesson}
                        disabled={!knowledgeChecksPassed}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-green-600/20"
                        data-testid="video-complete-btn"
                      >
                        Video gesehen <CheckCircle className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* QUIZ */}
                {!lessonLocked && (currentLesson.lesson_type === 'quiz' || currentLesson.lesson_type === 'final_quiz') && (
                  <div>
                    {currentLesson.lesson_type === 'final_quiz' && (
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                        <h3 className="text-purple-400 font-bold flex items-center gap-2">
                          <Trophy className="w-5 h-5" /> Abschlusstest
                        </h3>
                        <p className="text-gray-300 text-sm mt-1">
                          Sie benötigen mindestens 80% um zu bestehen. Bei Erfolg erhalten Sie Ihr Zertifikat!
                        </p>
                      </div>
                    )}
                    <QuizComponent 
                      questions={currentLesson.quiz_questions || []}
                      onSubmit={submitQuiz}
                      passThreshold={currentLesson.pass_threshold * 100}
                      onNavigateToLesson={(lessonId) => navigate(`/kurs/${moduleId}/${lessonId}`)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* CSS für Lesson Content */}
      <style>{`
        .lesson-content h2 { color: #eab308; font-size: 1.5rem; margin-bottom: 1rem; }
        .lesson-content h3 { color: #fff; font-size: 1.25rem; margin: 1.5rem 0 0.75rem; }
        .lesson-content h4 { color: #d1d5db; font-size: 1.1rem; margin: 1rem 0 0.5rem; }
        .lesson-content p { color: #9ca3af; line-height: 1.7; margin-bottom: 1rem; }
        .lesson-content ul, .lesson-content ol { color: #9ca3af; margin: 1rem 0; padding-left: 1.5rem; }
        .lesson-content li { margin-bottom: 0.5rem; }
        .lesson-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
        .lesson-content th { background: rgba(234, 179, 8, 0.1); color: #eab308; padding: 0.75rem; text-align: left; border: 1px solid #374151; }
        .lesson-content td { padding: 0.75rem; border: 1px solid #374151; color: #d1d5db; }
        .lesson-content .alert { padding: 1rem; border-radius: 0.5rem; margin: 1rem 0; }
        .lesson-content .alert-info { background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.3); }
        .lesson-content .alert-warning { background: rgba(234, 179, 8, 0.1); border: 1px solid rgba(234, 179, 8, 0.3); }
        .lesson-content .alert-danger { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); color: #fca5a5; }
        .lesson-content .info-box, .lesson-content .warning-box { padding: 1rem; border-radius: 0.5rem; margin: 1rem 0; background: rgba(234, 179, 8, 0.1); border: 1px solid rgba(234, 179, 8, 0.3); }
        .lesson-content .warning-box { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.3); }
        .lesson-content .card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0; }
        .lesson-content .card { background: rgba(0,0,0,0.3); padding: 1rem; border-radius: 0.5rem; border: 1px solid #374151; }
        .lesson-content .safety-rules { display: grid; gap: 1rem; margin: 1.5rem 0; }
        .lesson-content .rule { background: rgba(0,0,0,0.3); padding: 1.5rem; border-radius: 0.75rem; border-left: 4px solid #eab308; }
        .lesson-content .rule-number { display: inline-flex; width: 2rem; height: 2rem; background: #eab308; color: black; border-radius: 50%; align-items: center; justify-content: center; font-weight: bold; margin-right: 0.75rem; }
        .lesson-content .video-container { position: relative; padding-bottom: 56.25%; height: 0; margin: 1rem 0; }
        .lesson-content .video-container iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 0.5rem; }
      `}</style>
    </div>
  );
};

export default CoursePlayer;
